import { promptCount } from "./promptMetrics.js";

const MAX_CONCURRENT_GROUPS = 4;
const MAX_OUTPUT_COLUMNS = 127;

/**
 * Count each requested group in the backend before projecting its result rows.
 * Distinct counts cannot be added across leaves: separate capture sources can
 * place the same prompt in multiple leaves.
 */
export async function countPromptGroups({
    root,
    filter,
    timeRange,
    groups,
    dimensions = [],
    runQuery,
    rowsToObjects,
    valueAlias = "value",
    limit = 100000,
}) {
    if (groups.length === 0) return [];
    if (!Number.isSafeInteger(limit) || limit <= 0) {
        throw new Error("Prompt group row limit must be a positive integer");
    }
    if (dimensions.length + 1 > MAX_OUTPUT_COLUMNS) {
        throw new Error("Too many dimensions for prompt group counts");
    }

    const request = { root, filter, timeRange, dimensions, runQuery, rowsToObjects, valueAlias, limit };
    const chunkSize = MAX_OUTPUT_COLUMNS - dimensions.length;
    const chunks = [];
    for (let start = 0; start < groups.length; start += chunkSize) {
        chunks.push(groups.slice(start, start + chunkSize));
    }
    const results = await inWorkerPool(chunks, async (chunk) =>
        (await countGroupsTogether(request, chunk)) ?? countEachGroup(request, chunk));
    return results.flat();
}

async function inWorkerPool(items, run) {
    const results = new Array(items.length);
    let next = 0;
    let failed = false;
    async function worker() {
        while (!failed && next < items.length) {
            const index = next++;
            try {
                results[index] = await run(items[index]);
            } catch (error) {
                failed = true;
                throw error;
            }
        }
    }
    await Promise.all(Array.from({ length: Math.min(MAX_CONCURRENT_GROUPS, items.length) }, worker));
    return results;
}

async function countGroupsTogether({ root, filter, timeRange, dimensions, runQuery, rowsToObjects, valueAlias, limit }, groups) {
    const aliases = groups.map((_, index) => `group_${index}`);
    const spec = {
        root,
        timeRange,
        output: {
            dimensions,
            metrics: groups.map((group, index) => promptCount(aliases[index], "p", group.predicate || undefined)),
        },
        limit: dimensions.length ? limit : 1,
    };
    if (filter) spec.filter = filter;
    let rows;
    try {
        rows = rowsToObjects(await runQuery(spec));
    } catch (error) {
        if (rejectsJoinedMetricPredicate(error)) return null;
        throw error;
    }
    if (dimensions.length && rows.length >= limit) return null;
    const metricAliases = new Set(aliases);
    return groups.map((group, index) => rows.flatMap((row) => {
        const value = Number(row[aliases[index]]) || 0;
        if (dimensions.length && value === 0) return [];
        return [{
            ...Object.fromEntries(Object.entries(row).filter(([key]) => !metricAliases.has(key))),
            id: group.id,
            [valueAlias]: value,
        }];
    })).flat();
}

function rejectsJoinedMetricPredicate(error) {
    return error?.status === 400
        && String(error.responseText || error.message).includes("per-metric predicates must use the same alias as the metric");
}

async function countEachGroup({ root, filter, timeRange, dimensions, runQuery, rowsToObjects, valueAlias, limit }, groups) {
    const results = await inWorkerPool(groups.map((group, index) => ({ group, index })), async ({ group, index }) => {
        const alias = `group_${index}`;
        const filters = [filter, group.predicate].filter(Boolean);
        const spec = {
            root,
            timeRange,
            output: { dimensions, metrics: [promptCount(alias, "p")] },
            limit,
        };
        if (filters.length) {
            spec.filter = filters.length === 1 ? filters[0] : { andExpr: { children: filters } };
        }
        const rows = rowsToObjects(await runQuery(spec));
        if (rows.length >= limit) {
            throw new Error("Prompt group counts reached the result limit");
        }
        return rows.map((row) => ({
            ...Object.fromEntries(Object.entries(row).filter(([key]) => key !== alias)),
            id: group.id,
            [valueAlias]: Number(row[alias]) || 0,
        }));
    });
    return results.flat();
}

/**
 * Count groups whose predicates are row-local conditions on the `p` step, as
 * one filtered distinct-count metric per group. Every group shares one scan
 * (one query per MAX_OUTPUT_COLUMNS groups) instead of costing a query each.
 * The backend rejects joined aliases inside metric predicates; count those
 * with countPromptGroups.
 */
export async function countRowLocalPromptGroups({
    root,
    filter,
    timeRange,
    groups,
    runQuery,
    rowsToObjects,
    valueAlias = "value",
}) {
    const chunks = [];
    for (let start = 0; start < groups.length; start += MAX_OUTPUT_COLUMNS) {
        chunks.push(groups.slice(start, start + MAX_OUTPUT_COLUMNS));
    }
    const counted = await Promise.all(chunks.map(async (chunk) => {
        const spec = {
            root,
            timeRange,
            output: {
                dimensions: [],
                metrics: chunk.map((group, index) => promptCount(`group_${index}`, "p", group.predicate)),
            },
            limit: 1,
        };
        if (filter) spec.filter = filter;
        const [row = {}] = rowsToObjects(await runQuery(spec));
        return chunk.map((group, index) => ({ id: group.id, [valueAlias]: Number(row[`group_${index}`]) || 0 }));
    }));
    return counted.flat();
}
